import '../InstallerPanel.css';

import { CSSProperties, ClassNameMap } from '@material-ui/core/styles/withStyles';
import { INode, ISystem } from '../../../helpers/interfaces';
import React, { ChangeEvent } from 'react';
import { addSystem, closeNotification, generateExternalSystemId } from '../../../stores/actions/installerActions';

import SystemIcon from '../../../components/ShortStatus/SystemIcon';
import Button from '@material-ui/core/Button';
import Card from '../../../../components/Card/CardTS';
import CardBody from '../../../../components/Card/CardBody';
import CardHeader from '../../../../components/Card/CardHeaderTS';
import CardIcon from '../../../../components/Card/CardIcon';
import Loading from '../../../components/Loading/Loading';
import MenuItem from '@material-ui/core/MenuItem';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Edit from '@material-ui/icons/Edit';
import Autorenew from '@material-ui/icons/Autorenew';
import { connect } from 'react-redux';
import { emptyHeaderStyle } from '../../../helpers/CustomStyles';
import regularFormsStyle from '../../../../assets/jss/material-dashboard-pro-react/views/regularFormsStyleTS';
import utils from '../../../helpers/GlobalVariables';
import { SYSTEM_VERSIONS } from '../../../helpers/Systems';

const inputStyle: CSSProperties = {
	flexBasis: '33%',
	paddingRight: '20px',
	minWidth: '200px',
	flexGrow: 1,
	transition: '0.3s',
	marginBottom: '21px',
};

interface ICreateSystemFormProps {
	classes: ClassNameMap;
	disableSystemSubmit: boolean;
	mobileView: boolean;
	NewNodes: INode[];
	systems: ISystem[];
	systemSaveFail: boolean;
	systemSaveSuccessfully: boolean;
	addSystem: (systemName: string, systemVersion: string, currentExternalSystemId: string) => void;
	closeNotification: () => void;
	generateExternalSystemId: () => Promise<string | null>;
}
interface ICreateSystemFormState {
	name: string;
	systemNameValue: string;
	systemNameError: boolean;
	systemVersionValue: string;
	externalSystemNameValue: string;
	externalSystemNameError: boolean;
	// External ID of a WSN system is generated by the backend, unless entered manually
	externalIdManual: boolean;
	generatingExternalId: boolean;
	externalIdGenerationFailed: boolean;
}

// 12 characters: uppercase letters, digits, _ and - (generated like WMC did)
const EXTERNAL_SYSTEM_ID_REGEX = /^[A-Z0-9_-]{12}$/;

interface ISystemVersion {
	label: string;
	value: string;
}

class CreateSystemForm extends React.Component<ICreateSystemFormProps, ICreateSystemFormState> {
	state = {
		name: '',
		systemNameValue: '',
		systemNameError: false,
		systemVersionValue: '',
		externalSystemNameValue: '',
		externalSystemNameError: false,
		externalIdManual: false,
		generatingExternalId: false,
		externalIdGenerationFailed: false,
	};
	closeNotificationTimer = null;
	systemVersions: ISystemVersion[] = [
		{ label: 'WSN', value: SYSTEM_VERSIONS.W2.valueOf() },
		{ label: 'LoRa', value: SYSTEM_VERSIONS.W4.valueOf() },
	];

	componentWillMount() {
		this.props.closeNotification();
	}

	componentDidMount() {
		const { systemSaveFail, systemSaveSuccessfully, closeNotification } = this.props;
		if (systemSaveFail || systemSaveSuccessfully) {
			closeNotification();
		}
	}

	componentDidUpdate(prevProps: ICreateSystemFormProps) {
		const { systemSaveFail, systemSaveSuccessfully, closeNotification } = this.props;
		if (systemSaveFail || systemSaveSuccessfully) {
			this.closeNotificationTimer = setTimeout(closeNotification, 6000);
		}
		if (systemSaveSuccessfully && !prevProps.systemSaveSuccessfully) {
			this.setState({ systemNameValue: '', systemNameError: false });
			if (this.state.systemVersionValue === SYSTEM_VERSIONS.W2.valueOf()) {
				if (this.state.externalIdManual) {
					this.setState({ externalSystemNameValue: '', externalSystemNameError: false });
				} else {
					this.generateExternalId();
				}
			}
		}
	}

	generateExternalId = (): void => {
		this.setState({ generatingExternalId: true, externalIdGenerationFailed: false });
		this.props.generateExternalSystemId().then((id) => {
			this.setState({
				generatingExternalId: false,
				externalIdGenerationFailed: !id,
				externalSystemNameValue: id ?? '',
				externalSystemNameError: false,
			});
		});
	};

	toggleExternalIdManual = (): void => {
		if (this.state.externalIdManual) {
			this.setState({ externalIdManual: false });
			this.generateExternalId();
		} else {
			this.setState({ externalIdManual: true, externalIdGenerationFailed: false, externalSystemNameError: false });
		}
	};

	componentWillUnmount() {
		clearTimeout(this.closeNotificationTimer);
		this.closeNotificationTimer = null;
	}

	handleInputChange = (e: ChangeEvent): void => {
		const currentValue = (e.target as HTMLInputElement).value;
		const containsIncorrectSigns = utils.validateInputChars(currentValue);

		this.setState({ systemNameError: containsIncorrectSigns, systemNameValue: currentValue });
	};

	handleExternalSystemNameChange = (e: ChangeEvent): void => {
		const currentValue = (e.target as HTMLInputElement).value.toUpperCase();
		this.setState({ externalSystemNameValue: currentValue, externalSystemNameError: !EXTERNAL_SYSTEM_ID_REGEX.test(currentValue) });
	};

	button = (): JSX.Element => {
		const { disableSystemSubmit, systemSaveFail, systemSaveSuccessfully } = this.props;
		const { systemNameError, systemNameValue, externalSystemNameValue, externalSystemNameError, systemVersionValue, generatingExternalId } = this.state;
		if (disableSystemSubmit) {
			return (
				<div style={{ width: '100%', marginTop: '25px', marginBottom: '10px', paddingLeft: '10px', paddingTop: '5px' }}>
					<Loading transform={1} height={17} width={40} color='darkGray' />
				</div>
			);
		}
		return (
			<div style={{ width: '100%' }}>
				<Button
					style={{ marginTop: systemNameError ? '5px' : '25px', transition: 'color 0.3s' }}
					disabled={
						disableSystemSubmit
						|| systemNameError
						|| !systemNameValue.length
						|| systemSaveFail
						|| systemSaveSuccessfully
						|| externalSystemNameError
						|| !systemVersionValue
						|| (systemVersionValue === SYSTEM_VERSIONS.W2.valueOf() && (!EXTERNAL_SYSTEM_ID_REGEX.test(externalSystemNameValue) || generatingExternalId))
					}
					data-testid='createSystemButton'
					onClick={(e) => {
						const name = (document.getElementById('system_name') as HTMLInputElement).value.trim();
						if (name !== null) {
							this.setState({ name: name });
							const currentExternalSystemId = systemVersionValue === SYSTEM_VERSIONS.W2.valueOf() ? externalSystemNameValue : '';
							this.props.addSystem(systemNameValue, systemVersionValue, currentExternalSystemId);
						}
					}}
				>
					Create
				</Button>
			</div>
		);
	};

	renderMobileNotifications = (): JSX.Element => {
		const { systemSaveFail, systemSaveSuccessfully, mobileView } = this.props;
		const headerStyle = utils.getHeaderStyle(systemSaveFail);

		if (!mobileView) return null;
		if (systemSaveFail || systemSaveSuccessfully) {
			return (
				<h5
					className={systemSaveFail ? 'notification-open-create-system-fail' : 'notification-open-create-system-success'}
					style={headerStyle}
				>
					{systemSaveFail
						? 'The system could not be created. Maybe a system with the specified name already exists. '
						: 'System created successfully.'}
				</h5>
			);
		}
		return <div style={emptyHeaderStyle}></div>;
	};

	handleSystemVersionChanged = (event: ChangeEvent): void => {
		const { value } = event.target as HTMLInputElement;
		if (value !== SYSTEM_VERSIONS.W2.valueOf()) {
			return this.setState({ externalSystemNameError: false, systemVersionValue: SYSTEM_VERSIONS.W4.valueOf() });
		}
		this.setState({ systemVersionValue: value });
		if (!this.state.externalIdManual && !this.state.externalSystemNameValue) {
			this.generateExternalId();
		}
	};

	renderSystemVersionSelect() {
		const { classes, mobileView } = this.props;
		const { systemVersionValue } = this.state;
		let menuItems = this.systemVersions.map((systemVersion, i) => {
			return (
				<MenuItem
					classes={{
						root: classes.selectMenuItem,
						selected: classes.selectMenuItemSelected,
					}}
					value={systemVersion.value}
					key={`system-version-item-${i}`}
				>
					{systemVersion.label}
				</MenuItem>
			);
		});
		return (
			<TextField
				select
				label='System version'
				id='system_version'
				inputProps={{
					value: systemVersionValue,
					autoCapitalize: 'off',
				}}
				onChange={(e) => this.handleSystemVersionChanged(e)}
				style={{ ...inputStyle, flexBasis: mobileView ? '50%' : '33%' }}
			>
				{menuItems}
			</TextField>
		);
	}

	render() {
		const { classes, closeNotification, disableSystemSubmit, mobileView, systemSaveFail, systemSaveSuccessfully } = this.props;
		const {
			systemNameError,
			systemNameValue,
			systemVersionValue,
			externalSystemNameValue,
			externalSystemNameError,
			externalIdManual,
			generatingExternalId,
			externalIdGenerationFailed,
			name,
		} = this.state;
		const isWsn = systemVersionValue === SYSTEM_VERSIONS.W2.valueOf();
		const externalIdHelperText = externalIdGenerationFailed
			? 'Could not generate the ID'
			: externalSystemNameError
				? '12 characters: uppercase letters, digits, _ or -'
				: '';

		return (
			<Card className='createSystemForm'>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<SystemIcon />
					</CardIcon>
					<h4 className={classes.cardIconTitle}>Create System</h4>
				</CardHeader>
				<CardBody>
					<form onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', flexWrap: 'wrap' }}>
						<TextField
							label='System name'
							id='system_name'
							inputProps={{
								disabled: disableSystemSubmit,
								value: systemNameValue,
								maxLength: utils.defaultMaxInputLength,
								autoCapitalize: 'off',
							}}
							style={{ ...inputStyle, flexBasis: mobileView ? '50%' : '33%' }}
							onChange={(e) => this.handleInputChange(e)}
							error={systemNameError}
							helperText={systemNameError ? 'Incorrect name. Characters not allowed: <, >, /, \\, &, ; ' : ''}
						>
							{name}
						</TextField>
						{this.renderSystemVersionSelect()}
						<div style={{
							...inputStyle,
							opacity: isWsn ? 1 : 0.5,
							pointerEvents: isWsn ? 'auto' : 'none',
							flexBasis: mobileView ? '100%' : '33%',
						}}>
							<TextField
								label='External ID'
								id='external_system_id'
								// read only and slightly greyed while generated; the icon unlocks it for entering manually / goes back to generating
								disabled={!isWsn}
								inputProps={{
									value: isWsn ? externalSystemNameValue : '',
									maxLength: utils.maxW2SystemNameLength,
									autoCapitalize: 'characters',
									readOnly: isWsn && !externalIdManual,
									style: isWsn && !externalIdManual ? { color: '#777' } : undefined,
								}}
								InputProps={{
									endAdornment: isWsn ? (
										<InputAdornment position='end'>
											<IconButton
												size='small'
												disabled={generatingExternalId}
												onClick={this.toggleExternalIdManual}
												aria-label={externalIdManual ? 'Generate External ID' : 'Enter External ID manually'}
											>
												{externalIdManual ? <Autorenew fontSize='small' /> : <Edit fontSize='small' />}
											</IconButton>
										</InputAdornment>
									) : null,
								}}
								onChange={(e) => this.handleExternalSystemNameChange(e)}
								error={externalSystemNameError || externalIdGenerationFailed}
								helperText={externalIdHelperText}
								fullWidth
							>
								{name}
							</TextField>
						</div>

						{this.renderMobileNotifications()}
						{this.button()}
					</form>
				</CardBody>
				{!mobileView ? (
					<Snackbar
						close={true}
						closeNotification={() => closeNotification()}
						color={systemSaveFail ? 'danger' : 'success'}
						message={
							systemSaveFail
								? 'The system could not be created. Maybe a system with the specified name already exists.'
								: 'System created successfully.'
						}
						open={systemSaveFail || systemSaveSuccessfully}
						place='tc'
					/>
				) : null}
			</Card>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		disableSystemSubmit: state.dashboard.disableSystemSubmit,
		systems: state.dashboard.systems,
		NewNodes: state.dashboard.NewNodes,
		systemSaveSuccessfully: state.installer.systemSaveSuccessfully,
		systemSaveFail: state.installer.systemSaveFail,
		mobileView: state.dashboard.mobileView,
	};
};

const actions = { addSystem, closeNotification, generateExternalSystemId };
const CreateSystemFormWithStyles = regularFormsStyle(CreateSystemForm);

export default connect(mapStateToProps, actions)(CreateSystemFormWithStyles);
